import {
  EligibilityDataSourceError,
  NetworkTypeEnum,
  Note
} from '@app/core/models/eligibility.model';
import { EmbedMessageToParentStatus, IEmbedMessageToParent } from '@app/core/models/embed.model';

import { AppConstants } from '@app/core/constants/app.constants';
import { EligibilityDataSource } from '@app/core/models/data-source.model';
import { MessageConstants } from '@app/core/constants/message.constants';

type PrioritizeEligibilityErrorsInput = {
  isEligibilitySuccess: boolean;
  schedulingErrors?: string[] | null;
  dataSourceErrors?: EligibilityDataSourceError[] | null;
  appointmentErrors?: string[] | null;
};

type ErrorMessageInput = {
  errors: string[];
  schedulingErrors?: string[] | null;
  dataSourceErrors?: EligibilityDataSourceError[] | null;
};

type ErrorMessageInputWithClientFriendly = {
  errors: string[];
  schedulingErrors?: { message: string; isClientFriendly: boolean }[] | null;
  dataSourceErrors?: EligibilityDataSourceError[] | null;
};

export class SharedMethods {
  /**
   * Returns the array of error messages to be shown in UI
   *
   * @param args PrioritizeEligibilityErrorsInput
   * @returns array of error messages to be shown in UI
   */
  static prioritizeEligibilityErrors(args: PrioritizeEligibilityErrorsInput): string[] {
    // Scheduling Errors are the first in priority
    if (args.appointmentErrors && args.appointmentErrors.length > 0) {
      return args.appointmentErrors;
    } else if (args.schedulingErrors && args.schedulingErrors.length > 0) {
      return args.schedulingErrors;
    } else if (args.dataSourceErrors) {
      const errorSettings = AppConstants.eligibilityErrorSetting;
      const groupedErrors: Record<EligibilityDataSource, string[]> = {} as Record<
        EligibilityDataSource,
        string[]
      >;

      Object.values(EligibilityDataSource).forEach((dataSource) => {
        groupedErrors[dataSource] = [];
      });

      // Get the dataSource errors and group them
      args.dataSourceErrors.forEach((e) => {
        if (e.errors) {
          groupedErrors[e.dataSource] = e.errors.map((erObj) => erObj.text);
        }
      });

      // Loop through the priority order defined in the App Constants
      // This loop will proceed in priority order
      for (const order in errorSettings.priority) {
        const dataSource = errorSettings.priority[order];
        if (groupedErrors[dataSource].length > 0) {
          // If the code gets to this part
          // the error is returned for that datasource if the eligibility failed
          if (!args.isEligibilitySuccess) return groupedErrors[dataSource];
          // If the eligibility is success
          // and the datasource error is not supressed on eligibility success
          else {
            if (!errorSettings.secondaryDataSources.includes(dataSource))
              return groupedErrors[dataSource];
          }
        }
      }
    }

    return [];
  }

  static getEligibilityErrorMessage(args: ErrorMessageInput): string[] {
    const errorMessage: string[] = [];
    if (args.schedulingErrors && args.schedulingErrors.length > 0) {
      return [];
    } else if (args.dataSourceErrors) {
      args.dataSourceErrors.forEach((error) => {
        error.errors?.forEach((err) => {
          args.errors.forEach((e) => {
            if (e === err.text) {
              errorMessage.push(err.message ?? '');
            }
          });
        });
      });
      return errorMessage;
    }
    return [];
  }

  static getEligibilityErrorMessageWithClientFriendly(
    args: ErrorMessageInputWithClientFriendly
  ): { message: string; isClientFriendly: boolean }[] {
    const errorMessage: { message: string; isClientFriendly: boolean }[] = [];
    if (args.schedulingErrors && args.schedulingErrors.length > 0) {
      return args.schedulingErrors.map((error) => ({
        message: error.message,
        // TODO: Remove true after finish client friendly error message implementation
        isClientFriendly: true || error.isClientFriendly
      }));
    } else if (args.dataSourceErrors) {
      args.dataSourceErrors.forEach((error) => {
        error.errors?.forEach((err) => {
          args.errors.forEach((e) => {
            if (e === err.text) {
              errorMessage.push({
                message: err.message ?? '',
                // TODO: Remove true after finish client friendly error message implementation
                isClientFriendly:
                  true || (err.isClientFriendly === undefined ? true : err.isClientFriendly)
              });
            }
          });
        });
      });
      return errorMessage;
    }
    return [];
  }

  static prioritizeErrorToBeNotified(
    dataSourceErrors: EligibilityDataSourceError[]
  ): string | undefined {
    let errorToBeNotifiedPriorityTemp: number;
    let errorToBeNotified: string | undefined;

    // Get the dataSource errors and group them
    dataSourceErrors.forEach((e) => {
      if (e.errors) {
        e.errors.forEach((erObj) => {
          const setting =
            AppConstants.eligibilityErrorSetting.errorToBeNotifiedPriority[erObj.code];
          if (
            erObj.code &&
            setting &&
            (!errorToBeNotifiedPriorityTemp || errorToBeNotifiedPriorityTemp > setting?.priority)
          ) {
            errorToBeNotifiedPriorityTemp = setting.priority;
            errorToBeNotified = erObj.code;
          }
        });
      }
    });

    return errorToBeNotified;
  }

  static toPercent(a: number, b: number) {
    if (a === 0 || b === 0) {
      return 0.0;
    }
    return parseFloat(((a / b) * 100).toFixed(2));
  }

  static constructNote(note: Note): string {
    if (note.message && typeof note.message !== 'string') {
      note.message = note.message?.map((str, i) => `${i + 1}. ${str}`).join('<br>');
    }
    const message = note.type ? `${note.type}<br>${note.message || '-'}` : note.message;
    return message || '-';
  }

  static parseNetworkType(netType: string): NetworkTypeEnum {
    switch (netType) {
      case NetworkTypeEnum.inNetwork:
        return NetworkTypeEnum.inNetwork;

      case NetworkTypeEnum.outOfNetwork:
        return NetworkTypeEnum.outOfNetwork;

      case NetworkTypeEnum.networkNotApplicable:
        return NetworkTypeEnum.networkNotApplicable;

      default:
        return NetworkTypeEnum.networkNotApplicable;
    }
  }

  static parseNetTypeDisplayName(netType: string): string {
    switch (netType) {
      case NetworkTypeEnum.inNetwork:
        return 'In-Network';

      case NetworkTypeEnum.outOfNetwork:
        return 'Out-of-Network';

      case NetworkTypeEnum.networkNotApplicable:
        return 'Network-Not-Applicable';

      default:
        return 'Other';
    }
  }

  /**
   * Posts a message to the parent window based on given status.
   */
  static checkAndSendMessageToParent(status: EmbedMessageToParentStatus, message?: string) {
    switch (status) {
      case EmbedMessageToParentStatus.OnLoad:
        SharedMethods.postMessageToParent({
          status: EmbedMessageToParentStatus.OnLoad,
          response: {
            message: MessageConstants.embedMessageToParent.ComponentLoaded
          }
        });
        break;
      case EmbedMessageToParentStatus.Error:
        SharedMethods.postMessageToParent({
          status: EmbedMessageToParentStatus.Error,
          response: {
            message: message ?? MessageConstants.embedMessageToParent.UnexpectedError
          }
        });
        break;
      case EmbedMessageToParentStatus.SessionExpired:
        SharedMethods.postMessageToParent({
          status: EmbedMessageToParentStatus.SessionExpired,
          response: {
            message: MessageConstants.embedMessageToParent.SessionInvalid
          }
        });
        break;
    }
  }

  /**
   * Posts a message to the parent window from embedded content.
   */
  private static postMessageToParent(message: IEmbedMessageToParent) {
    window.parent.postMessage(message, '*');
  }

  static parseJsonString(jsonString: string) {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      return;
    }
  }

  static isJSON(value: string) {
    try {
      const obj = JSON.parse(value);
      return typeof obj === 'object' && obj !== null;
    } catch (e) {
      return false;
    }
  }
}
